import React from 'react';
import { useTranslation } from 'react-i18next';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';

function SystemKycPageView({
  rows,
  jurisdictions,
  selectedJurisdictionId,
  isJurisdictionsLoading,
  isLoading,
  isSaving,
  error,
  success,
  deleteTargetId,
  dragSourceId,
  setDeleteTargetId,
  setDragSourceId,
  setSelectedJurisdictionId,
  updateJurisdictionQuery,
  confirmDelete,
  handleDrop,
  toggleStepImmutable,
  openCreate,
  openView,
  openEdit,
}) {
  const { t } = useTranslation();

  return (
    <div>
      <FloatingAlerts
        info={isSaving ? t('kyc.saving') : ''}
        error={error}
        success={success}
      />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('kyc.title')}</h2>
          <p>{t('kyc.subtitle')}</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={openCreate}
          disabled={isLoading || isSaving || !selectedJurisdictionId}
        >
          {t('kyc.addStep')}
        </button>
      </div>

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <div style={{ minWidth: '280px', maxWidth: '420px', width: '100%' }}>
            <label className="form-label mb-1">{t('kyc.labelJurisdiction')}</label>
            <select
              className="form-select"
              value={selectedJurisdictionId}
              onChange={(event) => {
                const nextId = String(event.target.value || '').trim();
                setSelectedJurisdictionId(nextId);
                updateJurisdictionQuery(nextId);
              }}
              disabled={isJurisdictionsLoading || isSaving}
            >
              {jurisdictions.length === 0 ? <option value="">{t('kyc.noJurisdictions')}</option> : null}
              {jurisdictions.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="card card-body mt-3">
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
              <table className="table search-table align-middle text-nowrap mb-0">
                <thead className="header-item">
                  <tr>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }}></th>
                    <th style={{ width: '220px' }}>{t('kyc.colName')}</th>
                    <th style={{ width: '46%' }}>{t('kyc.colDescription')}</th>
                    <th style={{ width: '120px' }}>{t('kyc.colFields')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-center">{t('kyc.colImmutable')}</th>
                    <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">{t('kyc.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">{t('kyc.noStepsYet')}</td>
                    </tr>
                  ) : null}
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      draggable={!isSaving}
                      onDragStart={() => setDragSourceId(row.id)}
                      onDragEnd={() => setDragSourceId('')}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => handleDrop(row.id)}
                      onClick={() => openView(row.id)}
                      className={`app-clickable-row ${dragSourceId === row.id ? 'opacity-50' : ''}`}
                    >
                      <td className="text-nowrap">
                        <span className="text-muted" title={t('kyc.dragToReorder')}>
                          <i className="fa fa-grip-vertical"></i>
                        </span>
                      </td>
                      <td title={row.name || '-'}>{row.name || '-'}</td>
                      <td className="app-kyc-description-cell" title={row.description || '-'}>
                        <span className="app-ellipsis-cell">{row.description || '-'}</span>
                      </td>
                      <td>{Array.isArray(row.fields) ? row.fields.length : 0}</td>
                      <td className="text-center">
                        <button
                          type="button"
                          className={`btn btn-sm app-icon-toggle ${row.immutable ? 'is-active' : ''}`}
                          title={t('kyc.immutableStep')}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleStepImmutable(row);
                          }}
                          disabled={isSaving || !row.id}
                        >
                          <i className="fa fa-lock"></i>
                        </button>
                      </td>
                      <td className="text-nowrap text-end">
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            title={t('common.edit')}
                            onClick={(event) => {
                              event.stopPropagation();
                              openEdit(row.id);
                            }}
                            disabled={isSaving || !row.id}
                          >
                            <i className="fa fa-pen"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            title={t('common.delete')}
                            onClick={(event) => {
                              event.stopPropagation();
                              setDeleteTargetId(row.id);
                            }}
                            disabled={isSaving || !row.id}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </LoadingContainer>
        </div>
      </div>

      {deleteTargetId ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('kyc.deleteStepTitle')}</h5>
                  <button type="button" className="btn-close" onClick={() => setDeleteTargetId('')}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">{t('kyc.deleteStepConfirm')}</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-default" onClick={() => setDeleteTargetId('')} disabled={isSaving}>{t('common.cancel')}</button>
                  <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={isSaving}>{t('common.delete')}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      ) : null}
    </div>
  );
}

export default SystemKycPageView;
